<script lang="ts">
	import type { ImageBlock } from '../imageBlock.svelte';
	import type { SbMuxVideo } from '$lib/storyblok/types';
	import { checkVisibility } from '$lib/actions/checkVisibility';
	import type { ISbStoryData } from '@storyblok/js';
	import { editable } from '$lib/storyblok/editable';
	import Blok from '$lib/storyblok/blok.svelte';

	interface Props {
		content: ISbStoryData<{
			assets: Array<ISbStoryData<ImageBlock | SbMuxVideo>['content']>;
		}>['content'];
	}

	let { content }: Props = $props();

	let visible = false;
	let transformVar = $derived(`--translate-y: ${visible ? 0 : 1}`);
</script>

<section
	use:editable={content}
	use:checkVisibility={{ threshold: 0.3 }}
	onvisible={({ detail }) => (visible = detail)}
	class="mx-1 my-16 flex gap-1 max-lg:flex-wrap"
	style={transformVar}
>
	{#if content.assets.length > 0}
		<div class="h-full w-1/2 overflow-hidden rounded-lg max-lg:hidden">
			<Blok content={content.assets[0]} />
		</div>
	{/if}
	{#if content.assets.length > 2}
		<div class="aspect-3/2 h-full w-full overflow-hidden rounded-lg lg:hidden">
			<Blok content={content.assets[2]} />
		</div>
	{/if}

	{#if content.assets.length > 1}
		<div class="flex w-full gap-y-1 max-lg:contents lg:w-1/2 lg:flex-col">
			{#if content.assets.length > 0}
				<div class="aspect-3/4 flex-1 overflow-hidden rounded-lg lg:hidden">
					<Blok content={content.assets[0]} />
				</div>
			{/if}
			<div class="aspect-3/4 flex-1 overflow-hidden rounded-lg lg:aspect-3/2">
				<Blok content={content.assets[1]} />
			</div>
			{#if content.assets.length > 2}
				<div class="aspect-3/2 flex-1 overflow-hidden rounded-lg max-lg:hidden">
					<Blok content={content.assets[2]} />
				</div>
			{/if}
		</div>
	{/if}
</section>

<style lang="scss">
	section {
		:global {
			picture > img {
				transition-duration: 0.8s;
			}
		}

		& > div:nth-child(1) {
			:global(picture > img) {
				transform: scale(1.5) translate3d(0, calc(var(--translate-y, 0) * 100px), 0);
			}
		}

		& > div:nth-child(2) {
			:global(picture > img) {
				transform: scale(1.5) translate3d(0, calc(var(--translate-y, 0) * 64px), 0);
			}
		}

		& > div:nth-child(3) {
			:global(picture > img) {
				transform: scale(1.5) translate3d(0, calc(var(--translate-y, 0) * 36px), 0);
			}
		}
	}
</style>
